import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { User } from "@/types/auth";
import { authApi } from "@/features/auth/api/auth.api";

interface AuthState {
  user: User | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLocked: boolean;
  mfaPending: boolean;
  mfaChallengeToken: string | null;
  mfaMethod: string | null;
  /** @deprecated tokens are now in HttpOnly cookies — kept for demo mode only */
  accessToken: string | null;
  /** @deprecated tokens are now in HttpOnly cookies — kept for demo mode only */
  refreshToken: string | null;
  setAuth: (payload: {
    user: User;
    permissions: string[];
    /** @deprecated used only by demo mode which cannot set real cookies */
    accessToken?: string;
    /** @deprecated used only by demo mode which cannot set real cookies */
    refreshToken?: string;
  }) => void;
  /** @deprecated no-op in cookie mode; kept for demo mode backwards compat */
  setTokens: (accessToken: string, refreshToken: string) => void;
  setMfaPending: (token: string, method?: string | null) => void;
  clearMfa: () => void;
  lock: () => void;
  unlock: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        permissions: [],
        isAuthenticated: false,
        isLocked: false,
        mfaPending: false,
        mfaChallengeToken: null,
        mfaMethod: null,

        setAuth: ({ user, permissions, accessToken = null, refreshToken = null }) =>
          set({
            user,
            // Tokens stored only for demo mode (which can't set real cookies).
            // In production the access/refresh tokens live in HttpOnly cookies
            // that JS cannot read — do not persist them in the store.
            accessToken,
            refreshToken,
            permissions,
            isAuthenticated: true,
            isLocked: false,
            mfaPending: false,
            mfaChallengeToken: null,
            mfaMethod: null,
          }),

        // No-op in cookie mode; retained so demo mode (which passes fake tokens
        // through the store) continues to work without code changes elsewhere.
        setTokens: (accessToken, refreshToken) =>
          set({ accessToken, refreshToken }),

        setMfaPending: (token, method = null) =>
          set({ mfaPending: true, mfaChallengeToken: token, mfaMethod: method }),

        clearMfa: () =>
          set({ mfaPending: false, mfaChallengeToken: null, mfaMethod: null }),

        lock: () => set({ isLocked: true }),

        unlock: () => set({ isLocked: false }),

        logout: () =>
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            permissions: [],
            isAuthenticated: false,
            isLocked: false,
            mfaPending: false,
            mfaChallengeToken: null,
          }),
      }),
      {
        name: "skul-auth",
        partialize: (state) => ({
          user: state.user,
          // Tokens omitted from localStorage — they live in HttpOnly cookies.
          // Demo mode is the only caller that passes real token strings; those
          // are still stored so the demo token-based Axios interceptor works.
          // In production accessToken/refreshToken will always be null here.
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          permissions: state.permissions,
          isAuthenticated: state.isAuthenticated,
          mfaPending: state.mfaPending,
          mfaChallengeToken: state.mfaChallengeToken,
          mfaMethod: state.mfaMethod,
        }),
        merge: (persisted, current) => {
          const stored = ((persisted as Partial<AuthState>).permissions ?? []).filter(
            (p): p is string => typeof p === "string",
          );
          // DEV-ONLY: if authenticated but permissions are empty (e.g. backend was
          // down at login time), grant a full admin set so the dev UI stays
          // navigable. This must NEVER run in production — otherwise a tampered or
          // empty persisted store would silently unlock every screen (incl. the
          // platform root-portal). In production, empty stored permissions stay
          // empty and the backend remains the sole authority.
          const isAuthenticated = !!(persisted as Partial<AuthState>).isAuthenticated;
          const DEV_ADMIN_PERMISSIONS = import.meta.env.DEV && isAuthenticated && stored.length === 0
            ? [
                "admin:all",
                "transport:read", "transport:write", "transport:manage",
                "transport:dispatch", "transport:track", "transport:delete",
                "tenants:manage", "users:manage", "roles:manage",
                "students:read", "students:write", "staff:read", "staff:write",
                "academic-years:manage", "academic-terms:manage",
                "grade-levels:read", "grade-levels:write", "streams:read",
                "subjects:read", "subjects:write", "enrollment:manage",
                "timetable:read", "timetable:write", "grades:manage",
                "assessments:read", "assessments:write", "attendance:manage",
                "fees:manage", "invoices:manage", "payments:read", "payments:write",
                "notifications:manage", "announcements:manage",
                "messages:read", "messages:write",
                "reports:read", "reports:write", "reports:export",
                "exams:manage", "exam-results:read", "discipline:manage",
                "admissions:manage", "events:read", "events:write",
                "health:read", "health:write", "documents:read", "documents:write", "documents:delete", "documents:manage",
                "config:read", "config:write", "audit-logs:read",
                "facilities:manage", "hostel:manage",
                "payroll:manage", "leave:manage",
                "library:read", "library:write", "library:manage",
                "inventory:read", "inventory:write", "inventory:manage",
                "driver-portal:read", "driver-portal:write",
                "student-portal:read", "student-portal:write",
                "instructor-portal:read", "instructor-portal:write",
                "guardian-portal:read", "guardian-portal:write",
                "staff-portal:read", "staff-portal:write",
                "librarian-portal:read", "librarian-portal:write",
                "applicant-portal:read", "applicant-portal:write",
                "root-portal:read", "root-portal:write", "root-portal:manage",
                "platform-tenants:read", "platform-tenants:write", "platform-tenants:suspend", "platform-tenants:delete",
                "platform-billing:read", "platform-billing:write", "platform-billing:manage",
                "platform-analytics:read",
                "platform-config:read", "platform-config:write",
                "platform-audit:read",
                "platform-security:read", "platform-security:write",
                "platform-support:read", "platform-support:write",
                "platform-releases:read", "platform-releases:write",
              ]
            : stored;
          return {
            ...current,
            ...(persisted as Partial<AuthState>),
            permissions: DEV_ADMIN_PERMISSIONS,
          };
        },
      },
    ),
    { name: "auth" },
  ),
);

/**
 * Sign out the current session on the server before clearing local state.
 * Best-effort: the server call is fire-and-forget-with-await — if it fails
 * (network error, already-expired token, backend down) we swallow the error
 * and still clear local auth state so the user is never stuck signed in.
 *
 * Use this from UI-triggered logouts (user menu, command menu, lock screen,
 * idle timeout). The axios 401-interceptor's own call to `logout()` stays
 * local-only, since a mid-refresh-failure is not a safe place to fire
 * another authenticated request.
 */
export async function logoutWithServer() {
  const { refreshToken, logout } = useAuthStore.getState();
  // In production, the refresh_token cookie is sent automatically with the
  // request — no need to pass it in the body. In demo mode the token is a
  // fake string we skip (the demo server doesn't revoke anything).
  if (refreshToken !== "demo-refresh-token") {
    try {
      await authApi.logout();
    } catch {
      // Best-effort — ignore network/API errors, still clear local state.
    }
  }
  logout();
}
